// The chrome SLOTS (Phase X step 3 wave A) — barSlot generalized to the three
// named slots the ceiling owns: "bar" (TopBar), "timeline" (TimelineBand) and
// "dock" (BottomDock). Same design, same reasons:
//
// TWO contexts on purpose (the step-2 self-caught loop fix): the API context
// is a STABLE object consumed by publishers; the slot STATE context is
// consumed only by the frames. If publishers read the state, every publish
// would re-render the publisher, whose publish-per-commit effect would
// publish again — an infinite loop. Split, the publisher re-publishes only
// on its OWN renders and the frames re-render only on real content changes.
//
// MONOTONIC-ID GUARD (pre-flight contract, generalizing LensShell's
// registrar semantics): ids are assigned at publisher mount from a counter
// that only grows. A publish from an OLDER id never steals a slot from a
// newer one, and a retract only clears a slot if that id still owns it — so
// a late unmount/re-render of the EXITING page can never clobber the
// arriving page's chrome. Route swaps are exactly this race.
// KNOWN LIMIT (step-2 review, still true): a still-mounted LOWER-id
// publisher can never re-win a slot after a higher id retracts. Safe today —
// per slot, publishers are mutually exclusive per route, and LensShell is
// the ONLY timeline/dock publisher (pre-flight A2 topology ruling: the map's
// strip + dock route through its contribution bag precisely so no second
// publisher id ever exists to trip this guard).
//
// RETRACT HOLDS (wave B frame-hold contract): a publisher's unmount leaves
// the frame rendering the LAST content — geometry included — because a
// per-page publisher cannot hold anything through its own unmount, and
// clearing on retract would blink every frame on every route swap (the bar
// flash step 2 killed). What CLEARS a frame is the ARRIVING page's publish:
// every route publishes every slot each commit, nulls included (useChrome),
// so held content survives exactly the one-commit gap between pages. Held
// content carries the exiting page's handlers — the same acceptance the
// shell's gap-hold always made (a click lands on a no-op unmounted echo).
import { createContext, useContext, useMemo, useState } from "react";

export const SLOT_NAMES = ["bar", "timeline", "dock"];

const EMPTY_SLOT = { ownerId: 0, content: null };
const EMPTY_SLOTS = {
  bar: EMPTY_SLOT,
  timeline: EMPTY_SLOT,
  dock: EMPTY_SLOT,
};

const SlotsApiContext = createContext(null);
const SlotsStateContext = createContext(null);

let nextPublisherId = 1;
export const claimPublisherId = () => nextPublisherId++;

export const SlotsProvider = ({ children }) => {
  const [slots, setSlots] = useState(EMPTY_SLOTS);

  const api = useMemo(
    () => ({
      publish: (name, id, content) =>
        setSlots((s) => {
          const cur = s[name] || EMPTY_SLOT;
          if (id < cur.ownerId) return s;
          if (cur.ownerId === id && cur.content === content) return s;
          return { ...s, [name]: { ownerId: id, content } };
        }),
      // HOLD, not clear (see header): ownership stays with the retracting id
      // so the monotonic guard keeps working; only a newer publish replaces
      // the held content.
      retract: () => {},
    }),
    [],
  );

  return (
    <SlotsApiContext.Provider value={api}>
      <SlotsStateContext.Provider value={slots}>
        {children}
      </SlotsStateContext.Provider>
    </SlotsApiContext.Provider>
  );
};

// For PUBLISHERS (LensToolbar the bar registrar, LensShell for the bands):
// stable, never re-renders you.
export const useSlots = () => {
  const ctx = useContext(SlotsApiContext);
  if (!ctx) throw new Error("useSlots must be used within SlotsProvider");
  return ctx;
};

// For THE FRAMES (chrome/TopBar, chrome/TimelineBand, chrome/BottomDock)
// only. One combined state object: any slot publish re-renders all three
// frames, which is the same frequency as today — LensShell publishes its
// whole contribution on every commit either way.
export const useSlotState = (name) => {
  const ctx = useContext(SlotsStateContext);
  if (!ctx) throw new Error("useSlotState must be used within SlotsProvider");
  return ctx[name] || EMPTY_SLOT;
};
